<template>
  <Container class="jcsb-container--fullheight">
    <ItemListCollectionWrapper
      :collectionModule="storeModule"
      defaultSortBy="name"
      :refreshKey="0"
      rowsPerPageLocalStorageKey="03-10-2023-policy-group-templates-per-page"
    >
      <template v-slot="slotProps">
        <ItemListPagination
          :class="$style.groupPolicyTemplateList"
          :columns="columns"
          :currentPageNum="slotProps.currentPageNum"
          :isLoading="slotProps.loading"
          isExpandable
          :itemComponent="$options.PolicyGroupTemplateItemRow"
          :rowsPerPage="slotProps.rowsPerPage"
          :selectable="false"
          :showEmptyState="slotProps.showEmptyState"
          :sortBy="slotProps.sortBy"
          :sortDirection="slotProps.sortDirection"
          :tableData="slotProps.tableData"
          :totalCount="slotProps.totalCount"
          @pageChange="slotProps.handlePageChange"
          @sortColumn="slotProps.handleSortColumn"
        >
          <template v-slot:toolbar>
            <Grid
              :class="$style.toolbar"
              hasGap
            >
              <GridItem :gridSpan="6">
                <InputSearch
                  :focus="slotProps.shouldFocusSearch"
                  placeholder="Search"
                  :value="slotProps.getSearchTerm"
                  @input="(term) => handleEnterSearchTerm(term, slotProps.handleEnterSearchTerm)"
                />
              </GridItem>
              <GridItem
                :class="$style.countItemsLabel"
                :gridSpan="6"
              >
                {{ getTotalCountText(slotProps.totalCount) }}
              </GridItem>
            </Grid>
          </template>

          <template v-slot:emptyState>
            <NoSearchResults
              :ctaText="emptyState.ctaText"
              :instructions="emptyState.instructions"
              @click="handleCtaClick"
            />
          </template>
        </ItemListPagination>
      </template>
    </ItemListCollectionWrapper>
  </Container>
</template>

<script>
import {
  Container,
  Grid,
  GridItem,
  InputSearch,
  ItemListPagination,
  NoSearchResults,
  Types,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModuleMap,
} from 'client/util/Constants';
import { debounce, extend, values } from 'lodash';
import { mapActions } from 'vuex';
import ItemListCollectionWrapper from 'client/components/ItemList/ItemListCollectionWrapper.vue';
import PolicyGroupTemplateItemRow from 'client/groups/Configuration/PolicyGroupTemplateItemRow.vue';
import pluralize from 'pluralize';

const { Column } = Types;

export default {
  name: 'PolicyGroupTemplates',

  components: {
    Container,
    Grid,
    GridItem,
    InputSearch,
    ItemListCollectionWrapper,
    ItemListPagination,
    NoSearchResults,
  },

  props: {
    itemType: {
      type: String,
      required: true,
      validator: function validator(value) {
        return values(ItemTypeConsts).indexOf(value) !== -1;
      },
    },

    providerId: {
      type: String,
      default: '',
    },

    sortFields: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    return {
      emptyState: {
        ctaText: 'create a new policy group',
        instructions: 'If you didn\'t find the template you were looking for,',
      },
      columns: [
        Column({
          dataFieldName: 'name',
          label: 'Template Name',
          sortable: !!this.sortFields.includes('name'),
          width: '1fr',
        }),
        Column({
          dataFieldName: 'description',
          label: 'Description',
          sortable: !!this.sortFields.includes('description'),
          width: '3fr',
        }),
        Column({
          dataFieldName: '000',
          alignment: 'right',
          label: '',
          sortable: false,
          width: '.5fr',
        }),
      ],
    };
  },

  PolicyGroupTemplateItemRow,

  computed: {
    storeModule() {
      return StoreModuleMap[this.itemType];
    },
  },

  beforeCreate() {
    const storeModule = StoreModuleMap[this.$options.propsData.itemType];

    if (!storeModule) {
      throw new Error('Could not find a storeModule to populate the listing.');
    }

    if (this.$options.propsData.providerId) {
      extend(
        this.$options.methods,
        mapActions(storeModule, [
          'setProviderId',
        ]),
      );
    }
  },

  async created() {
    await this.handleCreated();
  },

  methods: {
    ...mapActions('AlertModule', [
      'triggerErrorAlert',
    ]),

    getTotalCountText(totalCount) {
      return `${totalCount} ${pluralize('group', totalCount)}`;
    },

    async handleCreated() {
      try {
        if (this.providerId && this.setProviderId) {
          this.setProviderId(this.providerId);
        }
      } catch (exp) {
        this.triggerErrorAlert('Unable to fetch policy group templates.');
      }
    },

    handleCtaClick() {
      this.$router.push(`${ItemTypeRoutes[ItemTypeConsts.configurationGroup]}/new`);
    },

    handleEnterSearchTerm: debounce(async (term, func) => {
      await func(term);
    }, 250),
  },
};
</script>

<style module scoped>
  .toolbar {
    align-items: center;
    min-height: 3em;
  }

  .countItemsLabel {
    color: var(--jc-color-disabled-text);
    text-align: left;
  }

  .groupPolicyTemplateList {
    --expandingContainer-backgroundColor: var(--jcWhite);
    --expandingContainer-maxHeight: 100%;
    --expandingContainer-padding: var(--jc-spacer) 10px;
    --transition-max-height: 900px;
  }
</style>
